/**
 * Chamadas HTTPS callable às Cloud Functions (Stripe Checkout — assinaturas).
 * Requer usuário autenticado (`context.auth` no backend).
 */

import type { PlanBillingPeriod } from '@/core/domain';
import {
  CLOUD_FUNCTION_ACTIVATE_FREE_PLAN,
  CLOUD_FUNCTION_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END,
  CLOUD_FUNCTION_CREATE_STRIPE_CHECKOUT_SESSION,
} from '@/core/config/firebase-functions';
import { callFirebaseFunction } from '@/core/services/functions/callable-functions.service';

export type CreateSubscriptionCheckoutRequest = {
  plan_id: string;
  billing_period: PlanBillingPeriod;
  /** Origem atual do app; backend usa na allowlist para success/cancel Stripe. */
  return_base_url?: string;
};

/** Resposta da callable `createStripeCheckoutSession`. */
export type CreateSubscriptionCheckoutResponse = {
  url: string;
  /** Id do documento `payments/{id}` (também na query da URL de sucesso). */
  payment_id: string;
};

/**
 * Cria sessão Stripe Checkout (assinatura) e retorna a URL de redirecionamento.
 */
export async function createSubscriptionCheckout(
  planId: string,
  billingPeriod: PlanBillingPeriod,
  options?: { return_base_url?: string }
): Promise<CreateSubscriptionCheckoutResponse> {
  const payload: CreateSubscriptionCheckoutRequest = {
    plan_id: planId,
    billing_period: billingPeriod,
  };
  if (options?.return_base_url && options.return_base_url.length > 0) {
    payload.return_base_url = options.return_base_url;
  }
  return callFirebaseFunction<CreateSubscriptionCheckoutRequest, CreateSubscriptionCheckoutResponse>(
    CLOUD_FUNCTION_CREATE_STRIPE_CHECKOUT_SESSION,
    payload
  );
}

/** Resposta da callable `cancelStripeSubscriptionAtPeriodEnd`. */
export type CancelSubscriptionAtPeriodEndResponse = {
  cancel_at_period_end: boolean;
  subscription_status: string;
};

/**
 * Agenda cancelamento da assinatura Stripe no fim do período pago.
 */
export async function cancelSubscriptionAtPeriodEnd(): Promise<CancelSubscriptionAtPeriodEndResponse> {
  return callFirebaseFunction<Record<string, never>, CancelSubscriptionAtPeriodEndResponse>(
    CLOUD_FUNCTION_CANCEL_STRIPE_SUBSCRIPTION_AT_PERIOD_END,
    {}
  );
}

export type ActivateFreePlanRequest = {
  plan_id: string;
};

export type ActivateFreePlanResponse = {
  ok: true;
};

/**
 * Ativa assinatura com plano gratuito (sem Stripe).
 */
export async function activateFreePlan(planId: string): Promise<ActivateFreePlanResponse> {
  return callFirebaseFunction<ActivateFreePlanRequest, ActivateFreePlanResponse>(
    CLOUD_FUNCTION_ACTIVATE_FREE_PLAN,
    { plan_id: planId }
  );
}
